import { useState } from "react";
import { ChevronDown, Loader2, Trophy } from "lucide-react";

import { useEntityCompetition } from "@/hooks/queries/useEntityCompetition";
import { formatMoneyCompact, formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Quién viene ganando las obras de esta entidad. Es el insumo más útil para
 * escribir el porqué de una recomendación, y no depende de la ficha de la
 * empresa ni de la IA: sale del histórico de adjudicaciones.
 *
 * Se carga al abrir: en un informe de 10 obras, pedir 10 históricos que quizá
 * nadie mire sería gratis para nosotros y lento para quien revisa.
 */
export function EntityCompetitionPanel({
  contractingId,
}: {
  contractingId: number;
}) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useEntityCompetition(contractingId, open);

  return (
    <div className="mt-3 border-t pt-3">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
      >
        <Trophy className="size-3.5" />
        Quién gana en esta entidad
        <ChevronDown
          className={cn("size-3.5 transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div className="mt-2">
          {isLoading && <Loader2 className="size-4 animate-spin" />}

          {data && data.winners.length === 0 && (
            <p className="text-xs text-muted-foreground">
              Sin obras adjudicadas por esta entidad en los últimos{" "}
              {data.monthsBack} meses.
            </p>
          )}

          {data && data.winners.length > 0 && (
            <>
              <p className="mb-2 text-[11px] text-muted-foreground">
                {formatNumber(data.totalWorks)} obra(s) ·{" "}
                {formatMoneyCompact(data.totalAmount)} en los últimos{" "}
                {data.monthsBack} meses
              </p>
              <ul className="space-y-1">
                {data.winners.map((winner) => (
                  <li
                    key={`${winner.name}-${winner.ruc}`}
                    className="flex items-baseline justify-between gap-3 text-xs"
                  >
                    <span className="truncate">
                      {winner.name}
                      {winner.isConsortium && (
                        <span className="ml-1.5 text-[10px] font-semibold uppercase text-muted-foreground">
                          consorcio
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      {formatMoneyCompact(winner.amount)} ·{" "}
                      {winner.works} obra{winner.works === 1 ? "" : "s"}
                    </span>
                  </li>
                ))}
              </ul>
              {data.topTwoShare >= 0.6 && (
                <p className="mt-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                  Dos empresas concentran el{" "}
                  {Math.round(data.topTwoShare * 100)}% del monto adjudicado por
                  esta entidad.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
